import type { ComponentProps } from "react";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";

import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  Lock,
  Minus,
  Plus,
} from "@components/icons";

import { cn } from "@/lib/utils";

// `lucide-react/dynamic` ships a registry of every Lucide icon (~1000)
// as a Promise-based dynamic-import table, which alone weighs ~975 KiB
// raw in the bundle. We only render four icons by name across the
// whole codebase (verified via grep of `startIcon=`/`endIcon=`), so a
// hand-rolled map gives us the same string-keyed API at a fraction of
// the bundle cost — tree-shaking discards every icon we don't list.
// When you need a new dynamic icon name, add it here AND export it
// from src/components/icons.ts.
const BUTTON_ICONS = {
  "arrow-right": ArrowRight,
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  lock: Lock,
  minus: Minus,
  plus: Plus,
} as const satisfies Record<string, LucideIcon>;

export type ButtonIconName = keyof typeof BUTTON_ICONS;

const buttonVariants = cva(
  "cursor-pointer font-mabry-pro-regular inline-flex items-center justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none rounded-full disabled:text-ui-coal text-white",
  {
    variants: {
      variant: {
        default: "font-medium bg-cta hover:bg-cta-hover disabled:bg-ui-garlic",
        secondary:
          "bg-white text-cta border-cta border hover:border-cta-hover hover:text-cta-hover disabled:border-ui-coal",
        ghost: "text-cta hover:text-cta-hover hover:bg-cta-opacity-3",
        link: "text-cta hover:bg-transparent hover:text-cta-hover hover:underline bg-transparent",
        icon: "text-cta hover:text-cta-hover hover:bg-cta-opacity-3",
        brand:
          "text-sm bg-[#F6F3F0] text-ui-pepper border border-ui-union font-medium",
        "brand-active": "text-sm bg-cta-active text-white border font-medium",
      },
      size: {
        default: "px-4 py-2",
        sm: "px-2 py-1 text-xs",
        lg: "h-[54px] px-10 font-bold text-xl",
        icon: "h-[32px] w-[32px]",
        link: "px-0",
        full: "h-10 px-6 w-full",
        brand: "px-5 py-2",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  startIcon?: ButtonIconName;
  endIcon?: ButtonIconName;
  rounded?: boolean;
  iconProps?: Omit<ComponentProps<LucideIcon>, "ref">;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      children,
      startIcon,
      endIcon,
      rounded = true,
      iconProps,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const StartIconCmp = startIcon ? BUTTON_ICONS[startIcon] : null;
    const EndIconCmp = endIcon ? BUTTON_ICONS[endIcon] : null;

    const content = (
      <>
        {isLoading && <Loader2 className="animate-spin absolute" />}
        <div
          className={`flex gap-2 items-center ${isLoading ? "opacity-0" : "opacity-100"}`}
        >
          {StartIconCmp && <StartIconCmp {...iconProps} />}
          {children}
          {EndIconCmp && <EndIconCmp {...iconProps} />}
        </div>
      </>
    );

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          !rounded && "rounded-lg",
        )}
        ref={ref}
        {...props}
        disabled={isLoading || props.disabled}
      >
        {asChild ? children : content}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
